import { Head, Link } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import SuperAdminLayout from './_SuperAdminLayout'

interface Summary {
  subscription_count: number
  active_count: number
  trial_count: number
  trial_conversion_denominator: number
  immediate_billing_count: number
  mrr_cents: number
  churned_count: number
  past_due_count: number
  read_only_count: number
}
interface Row {
  organization_id: number
  organization_name: string
  plan: string
  plan_key: string
  payer: string | null
  status: string
  trial_requested: boolean
  trial_ends_at: string | null
  period_end: string | null
  pending_payer_switch?: { from: string; to: string; created_at: string } | null
  scheduled_plan?: { plan: string; effective_at: string } | null
}
interface Props {
  inventory: InventoryData
  summary: Summary
  payer: { guide_count: number; company_count: number; pending_switch_count: number }
  subscriptions: Row[]
  card_blast_radius: { brand: string; last4: string; organization_count: number; organizations: string[] }[]
}
export interface BillingPagination {
  page: number
  per_page: number
  total_count: number
  total_pages: number
  search: string | null
  filters: Record<string, string>
}
interface InventoryData {
  companies: CompanyRow[]
  counts: { total: number; guide: number; company: number; unset: number }
  pagination: BillingPagination
}

const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export interface CompanyRow {
  id: number; name: string; selected_payer: string; effective_payer: string | null
  setup: string; subscription_status: string; enrollment_eligible: boolean; access: string
  protected: boolean; projection_updated_at: string | null; last_event_at: string | null
  unresolved: boolean; existing_obligation: boolean
  plan_key: string | null; plan_name: string | null; user_allowance: number | null
  active_users: number; published_chart_entries: number
  active_users_over_limit: boolean; published_chart_entries_over_limit: boolean
  pending_switch: { from: string; to: string; stage: string; setup_completed: boolean } | null
}
export const payerLabel = (payer: string | null) => payer === 'guide' ? 'Guide' : payer === 'company' ? 'Client' : 'Not set'

export default function SuperAdminBilling({ summary, payer, subscriptions, card_blast_radius, inventory }: Props) {
  return <SuperAdminLayout>
    <Head title="SuperAdmin · Billing report" />
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4"><div><h1 className="text-3xl font-bold text-text-primary">Billing report</h1><p className="text-text-muted mt-1">Operational reporting from the local billing projection. Stripe is not queried here.</p></div><Link href="/super_admin/stripe_setting" className="px-4 py-2 rounded-lg bg-pinnacle-red text-white text-sm font-semibold">Stripe settings</Link></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="MRR" value={money(summary.mrr_cents)} hint={`${summary.active_count} active or dunning`} />
        <Stat label="Trials" value={summary.trial_count} hint={`${summary.trial_conversion_denominator} in conversion denominator`} />
        <Stat label="Immediate billing" value={summary.immediate_billing_count} hint="Excluded from trial conversion" />
        <Stat label="Churned" value={summary.churned_count} hint={`${summary.past_due_count} past due · ${summary.read_only_count} read-only`} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Guide-paid" value={payer.guide_count} />
        <Stat label="Company-paid" value={payer.company_count} />
        <Stat label="Pending payer switches" value={payer.pending_switch_count} />
      </div>
      <Inventory inventory={inventory} />
      <section className="bg-white border border-stone/20 rounded-xl overflow-x-auto">
        <div className="p-5 border-b border-stone/10"><h2 className="text-lg font-bold">Subscriptions</h2><p className="text-sm text-text-muted">Trial choice, dunning state, and scheduled conflicts are kept visible.</p></div>
        <table className="w-full text-sm"><thead className="bg-stone/5 text-left text-xs uppercase tracking-wide text-text-muted"><tr>{['Organization', 'Plan', 'Payer', 'State', 'Trial decision', 'Renewal', 'Pending changes'].map(h => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone/10">{subscriptions.map(row => <tr key={row.organization_id}>
            <td className="p-3 font-semibold">{row.organization_name}</td><td className="p-3">{row.plan}</td><td className="p-3 capitalize">{row.payer || '—'}</td>
            <td className="p-3"><span className={row.status === 'past_due' ? 'text-red-700 font-semibold' : ''}>{row.status}</span></td>
            <td className="p-3">{row.trial_requested ? `30-day trial${row.trial_ends_at ? ` · ends ${row.trial_ends_at}` : ''}` : 'Bill immediately'}</td>
            <td className="p-3">{row.period_end || '—'}</td>
            <td className="p-3">{row.pending_payer_switch ? `Switch ${row.pending_payer_switch.from} → ${row.pending_payer_switch.to}` : row.scheduled_plan ? `${row.scheduled_plan.plan} · ${row.scheduled_plan.effective_at}` : '—'}</td>
          </tr>)}</tbody>
        </table>
      </section>
      <section className="bg-white border border-stone/20 rounded-xl p-5">
        <h2 className="text-lg font-bold">Card blast radius</h2><p className="text-sm text-text-muted mt-1">Local card fingerprints are intentionally limited to brand and last four digits.</p>
        {card_blast_radius.length === 0 ? <p className="text-sm text-text-muted mt-4">No shared saved payment methods detected.</p> : <ul className="mt-4 divide-y divide-stone/10">{card_blast_radius.map(card => <li key={`${card.brand}-${card.last4}`} className="py-3"><span className="font-semibold">{card.brand} ending in {card.last4}</span><span className="text-text-muted"> · {card.organization_count} organizations</span><div className="text-xs text-text-muted mt-1">{card.organizations.join(', ')}</div></li>)}</ul>}
      </section>
    </div>
  </SuperAdminLayout>
}

function Inventory({ inventory }: Pick<Props, 'inventory'>) {
  const [search, setSearch] = useState(inventory.pagination.search || '')
  const [filters, setFilters] = useState<Record<string, string>>(inventory.pagination.filters || {})
  const [page, setPage] = useState(inventory.pagination.page)
  const [result, setResult] = useState(inventory)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const initial = useRef(true)
  useEffect(() => {
    if (initial.current) {
      initial.current = false
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setError('')
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({ inventory_only: 'true', page: String(page), per_page: String(result.pagination.per_page), search })
      Object.entries(filters).forEach(([key, value]) => { if (value) query.set(`filters[${key}]`, value) })
      fetch(`/super_admin/billing?${query}`, { headers: { Accept: 'application/json' }, signal: controller.signal }) // allow-direct-fetch: read-only GET of billing inventory
        .then(async response => {
          const data = await response.json()
          if (!response.ok) throw new Error(data.error || 'Could not load company inventory')
          if (!data.inventory?.pagination) throw new Error('Company inventory response is missing pagination')
          if (controller.signal.aborted) return
          const next = data.inventory as InventoryData
          if (next.pagination.page > Math.max(1, next.pagination.total_pages)) {
            setPage(Math.max(1, next.pagination.total_pages))
          } else {
            setResult(next)
          }
        })
        .catch(error => { if (error.name !== 'AbortError') setError(error.message) })
        .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    }, search !== result.pagination.search ? 250 : 0)
    return () => { window.clearTimeout(timer); controller.abort() }
  // per_page is fixed by the initial response; changing result must not trigger another request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, filters])
  const { pagination } = result
  const rows = result.companies
  return <section className="bg-white border border-stone/20 rounded-xl p-5 space-y-4">
    <h2 className="text-lg font-bold">All-company inventory</h2>
    <p className="text-sm">Active users are active tenant-visible company memberships, excluding support identities, pending, inactive, and deleted users. Published chart entries count every non-Owners-Box seat across active published Results Ownership Charts, including vacant and externally assigned seats. Drafts and archived charts are excluded; people are not deduplicated across seats or charts.</p>
    <p className="text-sm">Each count is compared independently with the tier allowance; only counts above it are red. Not configured disables comparison and does not mean unlimited. Tiers reflect the local subscription and its displayed status, not future changes or a guarantee of active entitlement.</p>
    <p className="text-sm">All {result.counts.total} companies · Guide {result.counts.guide} · Client {result.counts.company} · Not set {result.counts.unset}. These counts include companies without subscriptions. Selected payer is not confirmed payment.</p>
    <div className="flex flex-wrap gap-3">
      <input aria-label="Search companies" placeholder="Search companies" value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} className="border rounded-lg p-2" />
      {[
        ['selected_payer', 'Payer', [['guide', 'Guide'], ['company', 'Client'], ['unset', 'Not set']]],
        ['setup', 'Setup', [['not_started', 'Not started'], ['pending', 'Pending'], ['confirmed', 'Confirmed']]],
        ['access', 'Access', [['full', 'Full'], ['read_only', 'Read only'], ['suspended', 'Suspended']]],
        ['enrollment_eligible', 'Enrollment', [['true', 'Eligible'], ['false', 'Not eligible']]],
        ['protected', 'Protection', [['true', 'Protected'], ['false', 'Not protected']]],
      ].map(([key, label, options]) => <select key={String(key)} aria-label={String(label)} value={filters[String(key)] || ''} onChange={e => { setFilters({ ...filters, [String(key)]: e.target.value }); setPage(1) }} className="border rounded-lg p-2">
        <option value="">All {String(label).toLowerCase()}</option>{(options as string[][]).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>)}
    </div>
    {error && <p role="alert" className="text-sm text-red-700">{error} <button type="button" className="underline" onClick={() => setFilters({ ...filters })}>Retry</button></p>}
    {loading ? <p role="status" className="text-sm text-text-muted">Loading companies…</p> : !error && <>
    <p className="text-sm">{pagination.total_count} matching companies</p>
    <div className="overflow-x-auto"><table className="w-full text-sm text-left"><thead><tr>{['Company', 'Tier / user allowance', 'Active users', 'Published chart entries', 'Selected / effective payer', 'Setup / subscription', 'Access / eligibility', 'Pending handoff', 'Local freshness'].map(label => <th className="p-3" key={label}>{label}</th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.id} className="border-t border-stone/20">
        <td className="p-3"><Link className="underline font-semibold" href={`/super_admin/billing/${row.id}`}>{row.name}</Link></td>
        <td className="p-3">{row.plan_name || 'No subscription'}{row.plan_name && <><br /><span>{row.subscription_status}</span></>}<br />Allowance: {row.user_allowance ?? 'Not configured'}</td>
        <UsageCount count={row.active_users} exceeded={row.active_users_over_limit} allowance={row.user_allowance} label="Active users" />
        <UsageCount count={row.published_chart_entries} exceeded={row.published_chart_entries_over_limit} allowance={row.user_allowance} label="Published chart entries" />
        <td className="p-3">{payerLabel(row.selected_payer)} / {payerLabel(row.effective_payer)}</td>
        <td className="p-3">{row.setup} / {row.subscription_status}</td>
        <td className="p-3">{row.access} {row.protected && '· Migration protected'}<br />{row.enrollment_eligible ? 'Eligible' : 'Not eligible'}</td>
        <td className="p-3">{row.pending_switch ? `${payerLabel(row.pending_switch.from)} → ${payerLabel(row.pending_switch.to)} · ${row.pending_switch.stage}` : 'None'}</td>
        <td className="p-3">{row.projection_updated_at || 'No subscription projection'}<br />{row.unresolved ? 'Unresolved operation / event' : 'Local data only; not a live Stripe check'}</td>
      </tr>)}</tbody>
    </table>{rows.length === 0 && <p className="p-3">No companies match these filters.</p>}</div>
    <div className="flex items-center gap-3 text-sm">
      <button type="button" className="border border-stone/30 rounded-lg px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
      <span>Page {pagination.page} of {pagination.total_pages || 1}</span>
      <button type="button" className="border border-stone/30 rounded-lg px-3 py-1 disabled:opacity-50" disabled={page >= pagination.total_pages} onClick={() => setPage(page + 1)}>Next</button>
    </div></>}
  </section>
}

function UsageCount({ count, exceeded, allowance, label }: { count: number; exceeded: boolean; allowance: number | null; label: string }) {
  return <td className="p-3"><span className={exceeded ? 'text-status-miss font-semibold' : ''}>{count}</span>{exceeded && <span className="sr-only">{label} exceed the configured allowance of {allowance}</span>}</td>
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return <div className="bg-white border border-stone/20 rounded-lg p-4"><div className="text-xs uppercase tracking-wide text-text-muted font-semibold">{label}</div><div className="text-2xl font-bold text-text-primary mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</div>{hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}</div>
}